<template>
  <PageBreadcrumbs :items="breadcrumbs" />
  <ContentCard id="campaign-new" class="mb-8" :loading="savingCampaignPersonalization">
    <template #header>
      <ContentCardSubheader @back="onCancel">
        {{ t('Reviews.product.campaignPersonalisation.new.title', { productName: product.title }) }}
        <template #actions>
          <button class="btn-outline-color-s mr-3" data-testid="reviews-pers-new-cancel" @click="onCancel">
            {{ t('Reviews.product.action.cancel') }}
          </button>
          <button
            class="btn-primary-color-s"
            data-testid="reviews-pers-new-submit"
            :disabled="savingCampaignPersonalization"
            @click="onSubmit"
          >
            {{ t('Reviews.product.action.addCampaignPersonalisation') }}
          </button>
        </template>
      </ContentCardSubheader>
    </template>
    <div class="mt-6 txt-body-m text-main desktop:w-4/5">
      {{ t('Reviews.product.campaign.form.inviteMessage.new.hint') }}
    </div>
    <div>
      <div v-for="(formData, row) in personalisationFormData" :key="formData.uuid" class="border-b border-main py-10 last:border-b-0">
        <div class="mb-3 flex justify-between">
          <h3 class="mb-3 txt-headline-xl">{{ `${row + 1}. link` }}</h3>
          <button
            v-if="row > 0"
            class="btn-outline-neutral-xs"
            data-testid="reviews-pers-new-remove"
            @click="removePersonalisationForm(row)"
          >
            {{ t('Reviews.product.action.removeCampaignPersonalisationForm') }}
            <Trash2Icon class="lucide-icon-s" />
          </button>
        </div>
        <ReviewsProductCampaignPersonalisationForm
          v-if="campaign"
          ref="personalizationForm"
          :campaign="detailsFormData"
          :initial-data="formData"
          :product="product"
          @change="(data) => onPersonalisationChange(data, row)"
        />
      </div>
    </div>
    <TooltipWrapper :key="`tooltip-${canAddPersonalisation}`" top class="w-full">
      <template #trigger>
        <div
          class="mb-6 h-40 w-full cursor-not-allowed content-center justify-items-center rounded-lg border border-main bg-surface-elevation-2"
          :class="canAddPersonalisation ? 'cursor-pointer' : 'cursor-not-allowed'"
          @click="addPersonalisationForm()"
        >
          <CirclePlusIcon class="lucide-icon-l text-icon-subtlest" />
          <div class="pt-2 text-headline-xs text-subtlest">{{ t('Reviews.product.action.addCampaignPersonalisationForm.headline') }}</div>
          <div class="pt-2 text-body-s text-subtlest">{{ t('Reviews.product.action.addCampaignPersonalisationForm.sub') }}</div>
        </div>
      </template>
      <template v-if="!canAddPersonalisation" #content>
        <div>{{ t('Reviews.product.campaign.maxPersonalisationItems.tooltip') }}</div>
      </template>
    </TooltipWrapper>
    <template #footer>
      <div class="flex flex-row items-center justify-end gap-x-3">
        <button class="btn-outline-color-s" data-testid="reviews-pers-new-cancel-foot" @click="onCancel">
          {{ t('Reviews.product.action.cancel') }}
        </button>
        <button class="btn-primary-color-s" data-testid="reviews-pers-new-submit-foot" @click="onSubmit">
          {{ t('Reviews.product.action.addCampaignPersonalisation') }}
        </button>
      </div>
    </template>
  </ContentCard>
  <EditCloseConfirmModal ref="confirmCloseModal" :back-to="backTo" />
</template>
<script setup lang="ts">
import { TooltipWrapper } from '@ramp106/omrjs-core-ui'
import { CirclePlusIcon, Trash2Icon } from '@ramp106/omrjs-core-ui/icons'
import { useVuelidate } from '@vuelidate/core'
import { v4 as uuidv4 } from 'uuid'
import { computed, reactive, useTemplateRef, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'

import PageBreadcrumbs from '@/components/PageBreadcrumbs.vue'
import EditCloseConfirmModal from '@/components/ReviewsManagement/EditCloseConfirmModal.vue'
import { useReviewsProductBreadcrumbs } from '@/composables/breadcrumbs/useReviewsProductBreadcrumbs.ts'
import {
  IncentivizationCampaignPersonalizationLocaleEnum,
  useCreateReviewsCampaignPersonalizationMutation,
  useReviewsIncentivizationCampaignQuery,
  type ProductData,
} from '@/gql/reviews'
import { showNotification } from '@/helpers/notificationHelper'
import { ReviewsRouteName } from '@/router/types'

import ReviewsProductCampaignPersonalisationForm from './ReviewsProductCampaignPersonalizationForm.vue'
import type { CampaignFormData, CampaignPersonalizationFormData } from './types'

const MAX_PERSONALISATIONS = 10

const props = defineProps<{
  product: ProductData
}>()

const { t, locale } = useI18n()
const route = useRoute()
const router = useRouter()
const breadcrumbs = useReviewsProductBreadcrumbs(props.product)
const confirmCloseModal = useTemplateRef('confirmCloseModal')
const v$ = useVuelidate()

const campaignId = route.params.campaignId as string
const { result, loading } = useReviewsIncentivizationCampaignQuery(
  computed(() => ({ campaignId })),
  { clientId: 'reviews' },
)

const campaign = computed(() => result.value?.manage?.incentivizationCampaign)

const canAddPersonalisation = computed(() => personalisationFormData.length < MAX_PERSONALISATIONS)

const detailsFormData = reactive<Pick<CampaignFormData, 'incentivizationType' | 'incentive' | 'amount'>>({
  incentive: false,
  incentivizationType: undefined,
  amount: undefined,
})

watch([loading, campaign], ([isLoading, campaignData]) => {
  if (!isLoading && !campaignData) {
    router.push({ name: '404' })
  }
  if (campaign.value) {
    detailsFormData.incentive = !!campaign.value.incentivizationsLimit
    detailsFormData.incentivizationType = campaign.value.incentivizationType
    detailsFormData.amount = campaign.value.amount
  }
})

const { mutate: createCampaignPersonalization, loading: savingCampaignPersonalization } = useCreateReviewsCampaignPersonalizationMutation({
  clientId: 'reviews',
})

const backTo = { name: ReviewsRouteName.ReviewsProductCampaignOverview, params: { campaignId: campaignId } }
const backToWithOpenPersonalisationSection = {
  name: ReviewsRouteName.ReviewsProductCampaignOverview,
  params: { campaignId: campaignId },
}

const personalisationFormData = reactive<CampaignPersonalizationFormData[]>([
  reactive({
    uuid: uuidv4(),
    locale: locale.value as IncentivizationCampaignPersonalizationLocaleEnum,
    inviteMessage: '',
  }),
])

function addPersonalisationForm() {
  if (canAddPersonalisation.value) {
    personalisationFormData.push(
      reactive({
        uuid: uuidv4(),
        locale: locale.value as IncentivizationCampaignPersonalizationLocaleEnum,
        inviteMessage: '',
      }),
    )
  } else {
    return
  }
}

function removePersonalisationForm(row: number) {
  if (personalisationFormData.length > 1) {
    personalisationFormData.splice(row, 1)
  } else {
    return
  }
}

function onPersonalisationChange(data: CampaignPersonalizationFormData, index: number) {
  Object.assign(personalisationFormData[index], data)
}

function onCancel() {
  if (v$.value.$anyDirty) {
    confirmCloseModal.value?.show()
  } else {
    router.push(backTo)
  }
}

async function onSubmit() {
  if (v$.value.$externalResults) {
    v$.value.$clearExternalResults()
  }

  if (v$.value.$invalid) {
    v$.value.$touch()
    return
  }

  if (campaignId) {
    const personalisationMutations = personalisationFormData.map((personalisation) =>
      createCampaignPersonalization({
        campaign: campaignId,
        attributes: {
          locale: personalisation.locale,
          inviteMessage: personalisation.inviteMessage,
        },
      }),
    )
    const responses = await Promise.allSettled(personalisationMutations)
    let errorCounter = 0
    const storedIndex: number[] = []
    responses.forEach((response, index) => {
      if (response.status === 'fulfilled') {
        if (
          response.value?.data?.manage.createCampaignPersonalization.errors &&
          response.value.data.manage.createCampaignPersonalization.errors.length > 0
        ) {
          errorCounter++
        } else {
          storedIndex.push(index)
        }
      } else if (response.status === 'rejected') {
        errorCounter++
      }
    })

    for (let i = storedIndex.length - 1; i >= 0; i--) {
      personalisationFormData.splice(storedIndex[i], 1)
    }

    if (errorCounter > 0) {
      showNotification(t('Reviews.product.campaignPersonalisation.new.genericErrorMessage'), 'error')
    } else {
      showNotification(t('Reviews.product.campaignPersonalisation.new.successMessage'), 'success')
      router.push(backToWithOpenPersonalisationSection)
    }
  }
}
</script>
